import { useEffect, useRef } from 'react';

export function InfinityScroll({
  children,
  onLoadMore,
  rootMargin = '100px',
  enabled = true,
}: {
  children?: React.ReactNode;
  onLoadMore: () => void;
  rootMargin?: string;
  /** When false, intersection does not trigger (e.g. no next page or fetch in flight). */
  enabled?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const intersectingRef = useRef(false);
  const prevEnabledRef = useRef<boolean | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        intersectingRef.current = entry.isIntersecting;
        if (entry.isIntersecting && enabledRef.current) {
          onLoadMoreRef.current();
        }
      },
      {
        rootMargin,
        threshold: 0.01,
      },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin]);

  // If we were blocked (e.g. loading) and become eligible while still in view, load again.
  useEffect(() => {
    const prev = prevEnabledRef.current;
    prevEnabledRef.current = enabled;
    if (prev === false && enabled === true && intersectingRef.current) {
      onLoadMoreRef.current();
    }
  }, [enabled]);

  return <div ref={ref}>{children}</div>;
}
